/**
 * Supporting evidence chapter: BDD-only pages that run after the Separation
 * Health Assessment flow.
 *
 * Flow (BDD claims only), after the Service treatment records pages:
 *   - /other-medical-records-bdd — single Yes/No about OTHER (non-VA) medical
 *      records. Reuses the shared evidence fields so the standard downstream
 *      flow handles both branches (V1 precedent: all-claims evidenceTypesBDD
 *      writes view:selectableEvidenceTypes for the standard pages to read):
 *        - Yes → standard private medical records flow
 *        - No  → standard "supporting documents and additional forms" flow
 */
import React from 'react';
import PropTypes from 'prop-types';
import {
  yesNoSchema,
  yesNoUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import { standardTitle } from '../../../form0781/content';
import { PROVIDER_FACILITIES_ARRAY_PATH } from '../../constants';
import { isBDD } from '../../utils/bdd';
import { confirmEvidenceRemovalPage } from '../../content/confirmEvidenceRemovalPage';
import {
  OtherMedicalRecordsBddDescription,
  otherMedicalRecordsBddQuestion,
} from '../../content/otherMedicalRecordsBdd';
import { PMR_UPLOAD_ARRAY_PATH } from './privateMedicalRecordsUploadPages';

/**
 * BDD: VA/military medical records are collected via Service Treatment
 * Records, so this page only drives the private-records branch. Mirror the
 * Yes/No answer into the shared fields the standard flow reads:
 *   - view:hasMedicalRecords        (wantsEvidenceRequest)
 *   - view:medicalRecordTypes.privateProvider (hasPrivateMedicalRecords)
 */
export const updateFormData = (oldData, newData) => {
  const answer = newData['view:hasMedicalRecords'];
  // Only mirror once the Veteran has answered. Mutating on page entry (before
  // an answer) dispatches a data change mid-navigation and bounces the route.
  if (answer !== true && answer !== false) {
    return newData;
  }
  return {
    ...newData,
    'view:medicalRecordTypes': {
      ...(newData['view:medicalRecordTypes'] || {}),
      vaOrMilitary: false,
      privateProvider: answer === true,
    },
  };
};

const getPrivateRecordsEvidence = formData => ({
  providers: formData?.[PROVIDER_FACILITIES_ARRAY_PATH] || [],
  uploads: formData?.[PMR_UPLOAD_ARRAY_PATH] || [],
});

// Confirms before deleting private records when a Veteran changes Yes to No.
export const OtherMedicalRecordsBddRemovalPage = confirmEvidenceRemovalPage({
  shouldConfirm: formData => {
    const { providers, uploads } = getPrivateRecordsEvidence(formData);
    return (
      formData?.['view:hasMedicalRecords'] === false &&
      (providers.length > 0 || uploads.length > 0)
    );
  },
  getItemGroups: formData => {
    const { providers, uploads } = getPrivateRecordsEvidence(formData);
    return [
      {
        intro: <p>We’ll remove these healthcare providers from your claim:</p>,
        items: providers.map(
          facility =>
            facility?.providerFacilityName || 'Private healthcare provider',
        ),
      },
      {
        intro: <p>We’ll delete these medical records you uploaded:</p>,
        items: uploads.map(
          upload => upload?.uploadedDocument?.name || 'Uploaded record',
        ),
      },
    ];
  },
  removeItems: formData => {
    const updated = { ...formData };
    delete updated[PROVIDER_FACILITIES_ARRAY_PATH];
    delete updated[PMR_UPLOAD_ARRAY_PATH];
    delete updated['view:privateMedicalRecordsAccess'];
    return updated;
  },
  revertSelection: formData => ({
    ...formData,
    'view:hasMedicalRecords': true,
  }),
  modalTitle: 'Change your medical records?',
  primaryButtonText: 'Change and delete',
  secondaryButtonText: 'Cancel change',
  alertText:
    'We’ve deleted the medical records information you provided for your claim.',
});

// A CustomPage skips the page-level updateFormData, so apply it here.
const OtherMedicalRecordsBddPage = props => (
  <OtherMedicalRecordsBddRemovalPage
    {...props}
    setFormData={newData =>
      props.setFormData(updateFormData(props.data, newData))
    }
  />
);

OtherMedicalRecordsBddPage.propTypes = {
  data: PropTypes.object,
  setFormData: PropTypes.func,
};

export const bddEvidencePages = {
  // BDD-only "Other medical records" decision page. Routes to the standard
  // private medical records flow (Yes) or the standard supporting documents
  // flow (No) via the shared fields set in updateFormData.
  otherMedicalRecordsBdd: {
    path: 'supporting-evidence/other-medical-records-bdd',
    title: 'Other medical records that support your disability claim',
    depends: formData => isBDD(formData),
    CustomPage: OtherMedicalRecordsBddPage,
    CustomPageReview: null,
    updateFormData,
    uiSchema: {
      'ui:title': standardTitle(
        'Other medical records that support your disability claim',
      ),
      'ui:description': OtherMedicalRecordsBddDescription,
      'view:hasMedicalRecords': yesNoUI({
        title: otherMedicalRecordsBddQuestion.label,
        labels: otherMedicalRecordsBddQuestion.labels,
      }),
    },
    schema: {
      type: 'object',
      required: ['view:hasMedicalRecords'],
      properties: {
        'view:hasMedicalRecords': yesNoSchema,
      },
    },
  },
};

export default bddEvidencePages;
